import type { SchemaInput } from '../shared/schema-input.js';
import { isNumber, isSchemaRecord, isString } from '../shared/runtime-values.js';
interface RoutePlannerWaypointDetails {
  label: string;
  address: string;
  query?: string;
}
export interface ResolvedRoutePlannerWaypoint extends RoutePlannerWaypointDetails {
  readonly lat: number;
  readonly lng: number;
}
export type RoutePlannerWaypointEntry = ResolvedRoutePlannerWaypoint
  | (RoutePlannerWaypointDetails & { readonly lat: null; readonly lng: null });

const ROUTE_PLANNER_LABELS = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ';

export function routePlannerWaypointLabel(index: number): string {
  return ROUTE_PLANNER_LABELS[index] ?? String(index + 1);
}

const WAYPOINT_COLORS = ['#2c6e49', '#e8640a', '#c0392b', '#7b2d8b', '#1a6ca8', '#8b7355'];

export function routePlannerWaypointColor(index: number, total: number): string {
  if (index === 0) return '#2c6e49';
  if (index === total - 1) return '#c0392b';
  return WAYPOINT_COLORS[index] ?? '#e8640a';
}

export function createBlankRoutePlannerWaypoint(label = ''): RoutePlannerWaypointEntry {
  return { label, address: '', lat: null, lng: null };
}

export function isResolvedRoutePlannerWaypoint(value: RoutePlannerWaypointEntry | undefined): value is ResolvedRoutePlannerWaypoint {
  return value !== undefined && value.lat !== null;
}

export function isEmptyRoutePlannerWaypointSlot(value: RoutePlannerWaypointEntry | undefined): boolean {
  return value === undefined || value.lat === null;
}

export function normalizeRoutePlannerWaypoint(value: SchemaInput, fallbackLabel = ''): RoutePlannerWaypointEntry {
  if (!isSchemaRecord(value)) {
    return createBlankRoutePlannerWaypoint(fallbackLabel);
  }

  const coordinates = isNumber(value.lat) && Number.isFinite(value.lat) && Math.abs(value.lat) <= 90
    && isNumber(value.lng) && Number.isFinite(value.lng)
    ? { lat: value.lat, lng: value.lng } : { lat: null, lng: null };
  const normalized: RoutePlannerWaypointEntry = {
    label: isString(value.label) ? value.label : fallbackLabel,
    address: isString(value.address) ? value.address : '',
    ...coordinates,
  };
  if (isString(value.query)) normalized.query = value.query;
  return normalized;
}

export function normalizeRoutePlannerWaypoints(values: ArrayLike<unknown>, minLength = 2): RoutePlannerWaypointEntry[] {
  const length = Math.max(values.length, minLength);
  return Array.from({ length }, (_, index) => ({
    ...normalizeRoutePlannerWaypoint(values[index]),
    label: routePlannerWaypointLabel(index),
  }));
}
